// react
import { useMemo } from 'react';

// mui
import { Autocomplete, Chip as MuiChip, TextField } from '@mui/material';

// components
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';
import SupportingText from '@/components/atoms/SupportingText/SupportingText';
import { Option } from '@/components/atoms/Select/Select';

export interface MultiSelectProps extends FieldProps<string> {
  options: Option[];
  isCustom?: boolean;
}

const CUSTOM_HINT = 'To add another option, type it and press Enter.';

const parseValueArray = (value: string | undefined): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

/**
 * MultiSelect lets the user pick multiple values from a dropdown. Wraps MUI
 * Autocomplete (multiple) so it picks up the surrounding theme variant. When
 * isCustom is true, the user can type free-text values not in the option list.
 */
export const MultiSelect = ({
  id,
  label,
  description,
  tooltip,
  value,
  onChange,
  options,
  placeholder,
  ariaLabel,
  isDisabled = false,
  isRequired = false,
  isHideTitle = false,
  isCustom = false,
}: MultiSelectProps) => {
  const valueArray = useMemo(() => parseValueArray(value), [value]);

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [options],
  );

  const optionValues = useMemo(
    () => sortedOptions.map((o) => o.value),
    [sortedOptions],
  );

  // Surface custom values that aren't in the predefined options so they
  // appear in the dropdown alongside the standard options.
  const allOptions = useMemo(() => {
    const extras = valueArray.filter((v) => !optionValues.includes(v));
    return [...optionValues, ...extras];
  }, [optionValues, valueArray]);

  const labelByValue = (v: string) =>
    sortedOptions.find((o) => o.value === v)?.label ?? v;

  return (
    <>
      <BaseField
        id={id}
        label={label}
        description={description}
        tooltip={tooltip}
        isRequired={isRequired}
        isHideTitle={isHideTitle}
      >
        <Autocomplete
          multiple
          freeSolo={isCustom}
          disabled={isDisabled}
          options={allOptions}
          value={valueArray}
          getOptionLabel={(opt) => labelByValue(opt as string)}
          onChange={(_, newValue) => {
            const next = (newValue as string[]).filter(Boolean);
            onChange?.(JSON.stringify(next));
          }}
          renderValue={(value, getItemProps) =>
            (value as string[]).map((option, index) => {
              const { key: _key, ...itemProps } = getItemProps({ index });
              return (
                <MuiChip
                  {...itemProps}
                  key={option}
                  label={labelByValue(option)}
                  size='small'
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={valueArray.length === 0 ? placeholder : ''}
              required={isRequired}
              slotProps={{
                ...params.slotProps,
                htmlInput: {
                  ...params.slotProps.htmlInput,
                  'aria-label': ariaLabel || label,
                },
              }}
            />
          )}
        />
      </BaseField>
      {isCustom && <SupportingText text={CUSTOM_HINT} icon={null} />}
    </>
  );
};

MultiSelect.componentName = 'MultiSelect';

export default MultiSelect;
